// frontend/src/api/useBossBattlesFeatureFlag.js
// Shared read of the `mode_boss_battle` rollout flag for every Boss Battles
// (Fields of Valhalla) surface in the app.
//
// WHY ONE HOOK RATHER THAN A FETCH PER ENTRY POINT: the option is surfaced from
// several places at once (the Configure-League "Boss Fight" card, the My Teams
// destination, and both routes). Each re-implementing the ACTIVE / INTERNAL+staff
// rule is how a nav entry and its route drift apart and a "hidden" mode stays
// reachable. Everything imports this, which in turn defers to the two API-layer
// helpers so the rule itself lives in exactly one place.
//
// THIS IS A RENDERING OPTIMIZATION ONLY. The server gate is authoritative — a
// user who gets past this (stale render, hand-typed route, flipped flag mid
// session) is still refused by the backend with the 404 envelope, which pages
// handle via `isBossBattlesFeatureDisabled`.

import { useState, useEffect } from 'react';
import { fetchBossBattlesFeatureFlag, isBossBattlesUiEnabled } from './bossBattlesApi';

// Explicit tri-state. `unknown` is NOT collapsed into `hidden`, even though both
// render nothing: callers that own a whole route need to draw a loader for
// `unknown` and an "unavailable" screen for `hidden`, and a boolean cannot tell
// them apart.
export const BOSS_BATTLES_VISIBILITY = {
    UNKNOWN: 'unknown',   // flag read still in flight
    VISIBLE: 'visible',   // ACTIVE, or INTERNAL and the user is staff
    HIDDEN: 'hidden'      // DISABLED / BETA / no flag row / read failed
};

/**
 * Read the rollout flag and evaluate it for the current user.
 *
 * DEFAULTS TO HIDDEN, ALWAYS. The initial state is `UNKNOWN` (which renders as
 * nothing, never as the option), so Boss Battles cannot flash visible on load
 * and then vanish — an off feature must never be advertised for a frame.
 *
 * A FAILED READ IS ALSO HIDDEN, and this is the one deliberate exception to the
 * never-silent-fail rule: "flag unreadable" and "flag off" are the same decision
 * for the UI (fail closed), and the alternative — throwing — would take down My
 * Teams and the join flow because one optional mode's flag endpoint hiccuped.
 * The failure is still logged loudly, never swallowed silently. Note the API
 * helper already maps the defined 404 "no such flag" answer to `DISABLED`, so
 * only genuine transport/contract failures reach the catch here.
 *
 * @param {Object|null} currentUser - Authenticated Django user (needs `is_staff`).
 * @returns {{visibility: string, isVisible: boolean, isResolved: boolean}}
 */
export function useBossBattlesFeatureFlag(currentUser) {
    const [visibility, setVisibility] = useState(BOSS_BATTLES_VISIBILITY.UNKNOWN);

    // `is_staff` is the ONLY user field the rule reads (INTERNAL admits staff
    // only), so the effect depends on that rather than on the user object —
    // AuthProvider hands down a fresh object on every auth refresh, and
    // depending on the identity would refetch the flag on each one.
    const isStaff = Boolean(currentUser && currentUser.is_staff);

    useEffect(() => {
        let active = true;

        fetchBossBattlesFeatureFlag()
            .then(flag => {
                if (!active) {
                    return;
                }
                const enabled = isBossBattlesUiEnabled(flag, { is_staff: isStaff });
                console.log(
                    `useBossBattlesFeatureFlag: state=${flag.state}, is_staff=${isStaff} → ` +
                    `${enabled ? 'VISIBLE' : 'HIDDEN'}`
                );
                setVisibility(enabled
                    ? BOSS_BATTLES_VISIBILITY.VISIBLE
                    : BOSS_BATTLES_VISIBILITY.HIDDEN);
            })
            .catch(error => {
                if (!active) {
                    return;
                }
                // Fail CLOSED and say so — see the docblock.
                console.error(
                    '❌ useBossBattlesFeatureFlag: could not read the mode_boss_battle flag — ' +
                    'hiding Boss Battles (fail closed). The server gate remains authoritative:',
                    error
                );
                setVisibility(BOSS_BATTLES_VISIBILITY.HIDDEN);
            });

        return () => {
            active = false;
        };
    }, [isStaff]);

    return {
        visibility,
        isVisible: visibility === BOSS_BATTLES_VISIBILITY.VISIBLE,
        isResolved: visibility !== BOSS_BATTLES_VISIBILITY.UNKNOWN
    };
}

export default useBossBattlesFeatureFlag;
